/**
 * <neutrix-voice-agent> Web Component (v1.1).
 *
 * Single inline-embed Web Component. The visual is a ChatGPT-style
 * audio-reactive particle sphere (see ui/particle-sphere.ts). Click to start
 * a conversation with Nova, click again to end it.
 *
 * No captions, no buttons, no chrome — the sphere's color and motion are
 * the entire UI signal. The host page reserves a square-ish container; we
 * fill it with a transparent canvas and let the host's background show.
 *
 * Owns:
 *   - Shadow-DOM-isolated DOM (one render at construction)
 *   - State machine (idle | connecting | listening | speaking | error)
 *   - ParticleSphere lifecycle
 *   - Click-to-toggle conversation
 *
 * Doesn't own:
 *   - SDK semantics (see conversation.ts)
 *   - Pixel-level rendering (see ui/particle-sphere.ts)
 */

import { NeutrixConversation, type ConvState } from './conversation';
import { config } from './config';
import { ParticleSphere } from './ui/particle-sphere';

const TEMPLATE = `
  <style>
    :host {
      display: block;
      width: 100%;
      height: 100%;
      contain: content;
      container-type: size;          /* lets us size text via cqi/cqmin */
      cursor: pointer;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;    /* removes iOS Safari 300ms tap delay */
    }
    .stage {
      position: relative;
      width: 100%;
      height: 100%;
      min-height: 200px;
      border-radius: inherit;
      overflow: hidden;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
    }
    /* Idle hint — small caption telling visitors the sphere is interactive.
       Visible only at rest; fades the moment a session starts so it doesn't
       compete with the live audio-reactive motion.
       Font size scales with the container (cqi = 1% of inline-size) so it
       stays balanced whether the slot is 200px or 600px. */
    .hint {
      position: absolute;
      inset: auto 0 14% 0;
      text-align: center;
      font: 500 clamp(10px, 3.4cqi, 13px)/1 ui-sans-serif, system-ui, -apple-system, sans-serif;
      letter-spacing: 0.06em;
      color: rgba(255, 255, 255, 0.55);
      pointer-events: none;
      opacity: 0;
      transition: opacity 280ms ease;
    }
    :host([data-state="idle"]) .hint {
      opacity: 1;
    }

    /* Error overlay — only visible when state="error". Subtle so it doesn't
       fight the sphere visually. */
    .retry {
      position: absolute;
      inset: auto 0 14% 0;
      text-align: center;
      font: 500 12px/1 ui-sans-serif, system-ui, -apple-system, sans-serif;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: rgba(254, 202, 202, 0.85);
      pointer-events: none;
      opacity: 0;
      transition: opacity 200ms ease;
    }
    :host([data-state="error"]) .retry {
      opacity: 1;
    }

    /* CTA card — shown when the agent calls schedule_demo. Floats below the
       sphere; clickable; persists for the rest of the session. We stop click
       propagation so opening the link doesn't also toggle the conversation. */
    .cta {
      position: absolute;
      left: 50%;
      bottom: 8%;
      transform: translate(-50%, 8px);
      display: inline-flex;
      align-items: center;
      gap: 10px;
      padding: 13px 22px;
      border-radius: 999px;
      background: linear-gradient(135deg, rgba(167, 139, 250, 0.22), rgba(56, 189, 248, 0.22));
      border: 1px solid rgba(255, 255, 255, 0.16);
      color: rgba(245, 243, 255, 0.95);
      font: 600 14px/1.2 ui-sans-serif, system-ui, -apple-system, sans-serif;
      letter-spacing: 0.01em;
      text-decoration: none;
      backdrop-filter: blur(10px);
      -webkit-backdrop-filter: blur(10px);
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      opacity: 0;
      pointer-events: none;
      transition: opacity 280ms ease, transform 280ms ease, border-color 160ms ease;
      white-space: nowrap;
    }
    .cta[data-visible="true"] {
      opacity: 1;
      transform: translate(-50%, 0);
      pointer-events: auto;
    }
    .cta:hover {
      border-color: rgba(255, 255, 255, 0.32);
      transform: translate(-50%, -2px);
    }
    .cta:focus-visible {
      outline: 2px solid rgba(165, 243, 252, 0.8);
      outline-offset: 3px;
    }
    .cta-arrow {
      opacity: 0.7;
      transition: transform 160ms ease;
    }
    .cta:hover .cta-arrow {
      transform: translateX(2px);
      opacity: 1;
    }
  </style>
  <div class="stage" part="stage">
    <canvas part="canvas"></canvas>
    <div class="hint" part="hint">Ask me anything</div>
    <div class="retry" part="retry">Tap to retry</div>
    <a class="cta" part="cta" data-visible="false"
       href="#" target="_blank" rel="noopener noreferrer">
      Schedule a demo<span class="cta-arrow" aria-hidden="true">→</span>
    </a>
  </div>
`;

export class NeutrixVoiceAgent extends HTMLElement {
  private readonly root: ShadowRoot;
  private readonly canvas: HTMLCanvasElement;
  private readonly stage: HTMLElement;
  private readonly cta: HTMLAnchorElement;
  private readonly conv = new NeutrixConversation();

  private sphere: ParticleSphere | null = null;
  private state: ConvState = 'idle';
  private unsubscribers: Array<() => void> = [];

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
    this.root.innerHTML = TEMPLATE;
    this.canvas = this.root.querySelector('canvas')!;
    this.stage = this.root.querySelector('.stage')!;
    this.cta = this.root.querySelector('a.cta')!;

    this.stage.addEventListener('click', () => {
      void this.toggle();
    });
    // CTA clicks must not toggle the conversation — let the link open and stop.
    this.cta.addEventListener('click', (ev) => ev.stopPropagation());

    // Wire conversation events. We subscribe in the constructor (per spec
    // we cannot touch host attributes here, but EventTarget subscription
    // is fine), and apply the initial state attribute later in connectedCallback.
    this.unsubscribers.push(
      this.conv.on('state', ({ state }) => this.applyState(state)),
      this.conv.on('level', ({ input, output }) => this.sphere?.setLevels(input, output)),
      this.conv.on('error', ({ message }) => {
        // Errors are surfaced via state="error"; just log the detail.
        // eslint-disable-next-line no-console
        console.warn('[neutrix-voice-agent] error:', message);
      }),
      this.conv.on('cta', ({ kind, url }) => {
        if (kind === 'schedule_demo') {
          this.cta.href = url;
          this.cta.dataset['visible'] = 'true';
        }
      }),
      this.conv.on('end', () => {
        // Reset CTA when a session ends — next session reveals it fresh
        // if the agent calls schedule_demo again.
        this.cta.dataset['visible'] = 'false';
      }),
    );
  }

  connectedCallback(): void {
    if (this.sphere === null) {
      this.sphere = new ParticleSphere(this.canvas);
      this.sphere.start();
    }
    // Safe to set data-* attributes here (post-construction).
    this.applyState(this.state);
  }

  disconnectedCallback(): void {
    this.sphere?.stop();
    this.sphere = null;
    void this.conv.stop();
  }

  // ---- public imperative API (kept minimal for tests/debugging) -------------

  async start(): Promise<void> {
    if (!config.agentId) {
      // No agent configured → stay idle. The host page is responsible for
      // setting VITE_NEUTRIX_AGENT_ID at build time.
      this.applyState('idle');
      return;
    }
    try {
      await this.conv.start({
        agentId: config.agentId,
        dynamicVariables: config.buildDynamicVariables(),
      });
    } catch {
      // Errors flow through the "error" event already; nothing to do.
    }
  }

  async stop(): Promise<void> {
    await this.conv.stop();
  }

  // ---- internals ------------------------------------------------------------

  private async toggle(): Promise<void> {
    if (this.state === 'idle' || this.state === 'error') {
      await this.start();
    } else {
      await this.stop();
    }
  }

  private applyState(state: ConvState): void {
    this.state = state;
    if (this.isConnected) {
      this.dataset['state'] = state;
    }
    this.sphere?.setState(state);
  }
}
